import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { getApiUrl } from '@/utils/api-config';
import { apiDelete, apiGet, apiPatch, apiPut } from '@/utils/api-interceptor';
import { isRecord } from '@/lib/records';

export interface LegalDocument {
  slug: string;
  title: string;
  content: string;
  is_active: boolean;
  doc_type?: string;
  created_at?: string;
  updated_at?: string;
}

export type LegalDocumentFields = Pick<LegalDocument, 'title' | 'content' | 'is_active'>;
type LegalDocumentAction =
  | { method: 'PUT'; slug: string; fields: LegalDocumentFields }
  | { method: 'PATCH'; slug: string; fields: Pick<LegalDocumentFields, 'is_active'> }
  | { method: 'DELETE'; slug: string };

const KEY = ['legal-documents'];
const ENDPOINT = 'admin/legal-documents/';
export const LEGAL_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

async function checkResponse(response: Response): Promise<void> {
  if (response.ok) return;
  let message = `Legal document request failed (${response.status}).`;
  const text = await response.text();
  if (text) {
    try {
      const body: unknown = JSON.parse(text);
      if (isRecord(body)) {
        if (typeof body.message === 'string') message = body.message;
        else if (typeof body.error === 'string') message = body.error;
        else if (isRecord(body.error) && typeof body.error.message === 'string') message = body.error.message;
      }
    } catch (error) {
      console.error('Could not parse legal document error response:', error);
    }
  }
  throw new Error(message);
}

function parseDocuments(body: unknown): LegalDocument[] {
  if (!isRecord(body) || !Array.isArray(body.data)) {
    throw new Error('The legal documents response did not contain a document list.');
  }
  return body.data.map((value: unknown) => {
    if (!isRecord(value) || typeof value.slug !== 'string' || !LEGAL_SLUG.test(value.slug) ||
      typeof value.title !== 'string' || typeof value.content !== 'string' || typeof value.is_active !== 'boolean') {
      throw new Error('The legal documents response contained an invalid document.');
    }
    return {
      slug: value.slug,
      title: value.title,
      content: value.content,
      is_active: value.is_active,
      doc_type: typeof value.doc_type === 'string' ? value.doc_type : undefined,
      created_at: typeof value.created_at === 'string' ? value.created_at : undefined,
      updated_at: typeof value.updated_at === 'string' ? value.updated_at : undefined,
    };
  });
}

export function useLegalDocuments() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: KEY,
    enabled: isAuthenticated,
    queryFn: async () => {
      const response = await apiGet(getApiUrl(ENDPOINT));
      await checkResponse(response);
      return parseDocuments(await response.json());
    },
  });
}

export function useSaveLegalDocument() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (action: LegalDocumentAction) => {
      if (!LEGAL_SLUG.test(action.slug)) throw new Error('Enter a slug using lowercase letters, numbers, and single hyphens.');
      const url = getApiUrl(`${ENDPOINT}${encodeURIComponent(action.slug)}/`);
      const response = action.method === 'DELETE'
        ? await apiDelete(url)
        : action.method === 'PUT'
          ? await apiPut(url, action.fields)
          : await apiPatch(url, action.fields);
      await checkResponse(response);
    },
    onSuccess: () => client.invalidateQueries({ queryKey: KEY }),
  });
}
